import { configureStore } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER
} from 'redux-persist';

import { userApi } from '@ports/internal-services/user/userApi';
import { subscriptionApi } from '@ports/internal-services/subscription/subscriptionApi';
import { userDetailsApi } from '@ports/internal-services/userDetails/userDetailsApi';
import { notificationSettingsApi } from '@ports/internal-services/notificationSettings/notificationSettingsApi';
import { paymentApi } from '@ports/internal-services/payment/paymentApi';
import { userAddressApi } from '@ports/internal-services/userAddress/userAddressApi';
import { utilityConfigurationApi } from '@ports/internal-services/utilityConfiguration/utilityConfigurationApi';
import { assetApi } from '@ports/internal-services/asset/assetApi';
import { folderApi } from '@ports/internal-services/folder/folderApi';
import { likesApi } from '@ports/internal-services/likes/likesApi';
import { commentLikesApi } from '@ports/internal-services/commentLikes/commentLikesApi';
import { diaryApi } from '@ports/internal-services/diary/diaryApi';
import { commentApi } from '@ports/internal-services/comment/commentApi';
import { favoritesApi } from '@ports/internal-services/favorites/favoritesApi';
import { privateApi } from '@ports/internal-services/private/privateApi';
import { photoTagApi } from '@ports/internal-services/photoTag/photoTagApi';
import { peopleApi } from '@ports/internal-services/people/peopleApi';
import { recipeApi } from '@ports/internal-services/recipe/recipeApi';
import { momentsApi } from '@ports/internal-services/moments/momentsApi';
import { momentDiaryApi } from '@ports/internal-services/momentDiary/momentDiaryApi';
import { trashApi } from '@ports/internal-services/trash/trashApi';
import { shareApi } from '@ports/internal-services/share/shareApi';
import { memoriesApi } from '@ports/internal-services/memories/memoriesApi';
import { userRoleApi } from '@ports/internal-services/userRole/userRoleApi';
import { inviteApi } from '@ports/internal-services/invite/inviteApi';
import { groupApi } from '@ports/internal-services/group/groupApi';
import { rootReducer, rootPersistConfig } from './reducer';

const persistedReducer = persistReducer(rootPersistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      },
      immutableCheck: false
    })
      .concat(userApi.middleware)
      .concat(subscriptionApi.middleware)
      .concat(userDetailsApi.middleware)
      .concat(notificationSettingsApi.middleware)
      .concat(paymentApi.middleware)
      .concat(userAddressApi.middleware)
      .concat(utilityConfigurationApi.middleware)
      .concat(commentApi.middleware)
      .concat(diaryApi.middleware)
      .concat(commentLikesApi.middleware)
      .concat(likesApi.middleware)
      .concat(assetApi.middleware)
      .concat(folderApi.middleware)
      .concat(favoritesApi.middleware)
      .concat(privateApi.middleware)
      .concat(photoTagApi.middleware)
      .concat(peopleApi.middleware)
      .concat(recipeApi.middleware)
      .concat(momentsApi.middleware)
      .concat(momentDiaryApi.middleware)
      .concat(trashApi.middleware)
      .concat(shareApi.middleware)
      .concat(memoriesApi.middleware)
      .concat(userRoleApi.middleware)
      .concat(inviteApi.middleware)
      .concat(groupApi.middleware)
});

export const persister = persistStore(store);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
